import { useCallback, useEffect, type RefObject } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useReducedMotion } from 'framer-motion';
import { queryClient } from '@/lib/queryClient';
import { restaurantKeys } from '@/api/keys/restaurantKeys';
import { seedRestaurantProfileIfComplete } from '@/utils/seedRestaurantProfile';
import { isRestaurantProfileChunkReady, warmRestaurantProfileChunk } from '@/utils/restaurantProfileChunk';
import {
  assignViewTransitionName,
  cancelBackTransition,
  getRestaurantTransitionNames,
} from '@/utils/viewTransition';

/**
 * Warms the profile chunk once per session (the single idle scheduler lives in
 * `warmRestaurantProfileChunk`; repeated calls are no-ops). Call from any
 * surface that can open a restaurant profile.
 */
export function useWarmRestaurantProfileChunk(enabled = true): void {
  useEffect(() => {
    if (enabled) warmRestaurantProfileChunk();
  }, [enabled]);
}

export interface OpenRestaurantProfileOptions {
  /**
   * The payload the surface holds for this restaurant. Seeded into the detail
   * cache only if it passes `isSeedableRestaurant` (a complete, member-specific,
   * fresh `Restaurant`). Omit it for partial payloads and for surfaces whose
   * data may be stale; the profile then takes the cold path (loader awaits
   * `/summary`). Never pass a hand-built object here.
   */
  seedFrom?: unknown;
  /** Elements to name for the shared-element photo/title morph. Both are needed to morph. */
  photoRef?: RefObject<HTMLElement | null>;
  titleRef?: RefObject<HTMLElement | null>;
}

/**
 * The one way a surface opens a restaurant profile. Order matters and matches
 * what `RestaurantCard` always did:
 * 1. `cancelBackTransition()`: a tap inside the ~800ms post-back cleanup window
 *    would otherwise play the reversed slide and lose the names set below.
 * 2. Seed the detail cache when `seedFrom` is a complete `Restaurant`.
 * 3. Morph only when ALL hold: reduced motion is off, the profile chunk is
 *    loaded, the detail cache holds data (the same predicate the route loader
 *    uses for its warm path), and both refs were given. Otherwise the router
 *    would capture the pending skeleton (or a not-yet-loaded chunk) as the
 *    transition's "new" snapshot and the morph would be lost.
 * 4. Names are assigned only when a transition will actually run.
 * 5. Surfaces with no refs (modals, text links) navigate without requesting a
 *    view transition, which is what they did before.
 *
 * Analytics stay at the call sites. `warm` (default true) warms the profile
 * chunk when the surface mounts.
 */
export function useOpenRestaurantProfile({ warm = true }: { warm?: boolean } = {}) {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  useWarmRestaurantProfileChunk(warm);

  return useCallback(
    (restaurantId: number, { seedFrom, photoRef, titleRef }: OpenRestaurantProfileOptions = {}) => {
      cancelBackTransition();
      seedRestaurantProfileIfComplete(seedFrom);

      const hasMorphTargets = photoRef != null && titleRef != null;
      const canMorph =
        hasMorphTargets &&
        !prefersReducedMotion &&
        isRestaurantProfileChunkReady() &&
        queryClient.getQueryData(restaurantKeys.detail(restaurantId)) !== undefined;

      if (canMorph) {
        // Not cleared afterward: the origin unmounts with the route change and
        // the names are scoped to this restaurant's id.
        const { photo, title } = getRestaurantTransitionNames(restaurantId);
        assignViewTransitionName(photoRef, photo);
        assignViewTransitionName(titleRef, title);
      }

      void navigate({
        to: '/restaurants/$restaurantId',
        params: { restaurantId },
        ...(hasMorphTargets ? { viewTransition: canMorph } : {}),
      });
    },
    [navigate, prefersReducedMotion],
  );
}
